import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Popconfirm, Segmented, Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Images, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { slidersApi } from '@api/sliders';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { SliderFormModal } from '../components/SliderFormModal';
import { SliderImagesDrawer } from '../components/SliderImagesDrawer';
import {
  WIDGET_PLACEMENTS,
  type SliderCreateInput,
  type SliderListItem,
  type SliderUpdateInput,
} from '../types';

const { Text } = Typography;
type PlacementFilter = 'ALL' | (typeof WIDGET_PLACEMENTS)[number];

export function SlidersPage() {
  const { t } = useTranslation(['widgets', 'common']);
  const td = t as unknown as (key: string) => string;
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.SLIDER_MANAGE);

  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [placement, setPlacement] = useState<PlacementFilter>('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SliderListItem | null>(null);
  const [imagesFor, setImagesFor] = useState<SliderListItem | null>(null);

  const { data: languages = [] } = useActiveLanguages();
  const langCodeById = useMemo(() => new Map(languages.map((l) => [l.id, l.code])), [languages]);

  const { data: sliders = [], isLoading } = useQuery({
    queryKey: ['sliders', { languageId, placement }],
    queryFn: () =>
      slidersApi.list({ languageId, placement: placement === 'ALL' ? undefined : placement }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sliders'] });

  const createMutation = useMutation({
    mutationFn: (input: SliderCreateInput) => slidersApi.create(input),
    onSuccess: async () => {
      toast.success(t('slider.toast.created'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: SliderUpdateInput }) =>
      slidersApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('slider.toast.updated'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (row: SliderListItem) => slidersApi.setPublished(row.id, row.status !== 'Published'),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => slidersApi.remove(id),
    onSuccess: async () => {
      toast.success(t('slider.toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (row: SliderListItem) => {
    setEditing(row);
    setModalOpen(true);
  };

  const columns: ColumnsType<SliderListItem> = [
    {
      title: t('fields.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setImagesFor(row)}>
          {name}
        </Button>
      ),
    },
    {
      title: t('fields.placement'),
      dataIndex: 'placement',
      key: 'placement',
      render: (p: string) => <Tag>{p ? td(`placement.${p}`) : '—'}</Tag>,
    },
    {
      title: t('fields.language'),
      key: 'language',
      render: (_, r) =>
        r.languageId ? <Tag color="blue">{langCodeById.get(r.languageId) ?? r.languageId.slice(0, 8)}</Tag> : null,
    },
    {
      title: t('fields.imageCount'),
      dataIndex: 'imageCount',
      key: 'imageCount',
      align: 'right',
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>,
    },
    {
      title: t('fields.status'),
      key: 'status',
      render: (_, r) => (
        <Space size={8}>
          <Switch
            size="small"
            checked={r.status === 'Published'}
            disabled={!canManage || publishMutation.isPending}
            onChange={() => publishMutation.mutate(r)}
            aria-label={t('fields.status')}
          />
          <Tag color={r.status === 'Published' ? 'green' : 'default'}>
            {r.status === 'Published' ? t('status.published') : t('status.draft')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('fields.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Images size={15} />}
            onClick={() => setImagesFor(r)}
            aria-label={t('slider.manageImages')}
            title={t('slider.manageImages')}
          />
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            aria-label={t('common:actions.edit')}
          />
          <Popconfirm
            title={t('slider.delete.title')}
            description={t('slider.delete.confirm', { name: r.name })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r.id)}
            disabled={!canManage}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={15} />} disabled={!canManage} aria-label={t('common:actions.delete')} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('slider.title')}
        description={t('slider.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('slider.title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('slider.new')}
            </Button>
          ) : null
        }
      />
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <Segmented<PlacementFilter>
              size="small"
              value={placement}
              onChange={setPlacement}
              options={[
                { value: 'ALL', label: t('common:labels.all') },
                ...WIDGET_PLACEMENTS.map((p) => ({ value: p, label: td(`placement.${p}`) })),
              ]}
            />
            <LanguagePicker
              value={languageId}
              onChange={setLanguageId}
              allowClear
              placeholder={t('common:contentLanguage.filterAll')}
              style={{ minWidth: 180 }}
            />
          </div>
          <Table<SliderListItem>
            rowKey="id"
            columns={columns}
            dataSource={sliders}
            loading={isLoading}
            pagination={false}
            locale={{ emptyText: <Text type="secondary">{t('slider.empty')}</Text> }}
          />
        </Space>
      </Card>

      <SliderFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />

      <SliderImagesDrawer
        open={imagesFor !== null}
        sliderId={imagesFor?.id ?? null}
        sliderName={imagesFor?.name}
        canManage={canManage}
        onClose={() => setImagesFor(null)}
        onChanged={() => void invalidate()}
      />
    </div>
  );
}
